(() => {
  if (window.__CORGI_COOKIE_CONSENT_LOADED__) return;
  window.__CORGI_COOKIE_CONSENT_LOADED__ = true;

  const ROOT_SELECTOR = '[data-corgi-cookie-consent]';
  const ACCEPT_SELECTOR = '[data-corgi-cookie-consent-accept]';
  const DECLINE_SELECTOR = '[data-corgi-cookie-consent-decline]';
  const STATUS_SELECTOR = '[data-corgi-cookie-consent-status]';
  const CONSENT_KEYS = ['analytics', 'marketing', 'preferences'];
  const LOCAL_CONSENT_KEY = 'corgi_cookie_consent';
  const LOCAL_CONSENT_COOKIE = 'corgi_cookie_consent';
  const LOCAL_CONSENT_MAX_AGE = 60 * 60 * 24 * 365;
  const VISIBLE_CLASS = 'is-visible';
  const HIDING_CLASS = 'is-hiding';

  function warn(message, error) {
    if (!window.console || typeof window.console.warn !== 'function') return;
    window.console.warn(`[Corgi cookie consent] ${message}`, error || '');
  }

  function loadCustomerPrivacy() {
    return new Promise((resolve, reject) => {
      const shopify = window.Shopify;

      if (!shopify) {
        reject(new Error('Shopify global is not available.'));
        return;
      }

      if (shopify.customerPrivacy) {
        resolve(shopify.customerPrivacy);
        return;
      }

      if (typeof shopify.loadFeatures !== 'function') {
        reject(new Error('Shopify.loadFeatures is not available.'));
        return;
      }

      shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        (error) => {
          if (error) {
            reject(error);
            return;
          }

          if (shopify.customerPrivacy) {
            resolve(shopify.customerPrivacy);
            return;
          }

          reject(new Error('Customer Privacy API did not load.'));
        }
      );
    });
  }

  function normalizeConsentFlag(value) {
    if (value === true || value === 'true' || value === 'yes') return true;
    if (value === false || value === 'false' || value === 'no') return false;
    return null;
  }

  function normalizeStoredConsent(record) {
    if (!record || typeof record !== 'object') return null;

    const normalized = {};
    for (const key of CONSENT_KEYS) {
      const value = normalizeConsentFlag(record[key]);
      if (value == null) return null;
      normalized[key] = value;
    }

    return {
      ...normalized,
      source: String(record.source || 'local-fallback'),
      savedAt: String(record.savedAt || ''),
    };
  }

  function readCookieConsent() {
    const cookie = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(`${LOCAL_CONSENT_COOKIE}=`));

    if (!cookie) return null;

    try {
      return normalizeStoredConsent(JSON.parse(decodeURIComponent(cookie.split('=').slice(1).join('='))));
    } catch (error) {
      warn('Local consent cookie could not be read.', error);
      return null;
    }
  }

  function readLocalConsent() {
    try {
      const stored = window.localStorage?.getItem(LOCAL_CONSENT_KEY);
      if (stored) {
        const normalized = normalizeStoredConsent(JSON.parse(stored));
        if (normalized) return normalized;
      }
    } catch (error) {
      warn('Local consent storage could not be read.', error);
    }

    return readCookieConsent();
  }

  function writeLocalConsent(consent) {
    const record = {
      analytics: Boolean(consent.analytics),
      marketing: Boolean(consent.marketing),
      preferences: Boolean(consent.preferences),
      source: 'local-fallback',
      savedAt: new Date().toISOString(),
    };
    const encoded = encodeURIComponent(JSON.stringify(record));

    try {
      window.localStorage?.setItem(LOCAL_CONSENT_KEY, JSON.stringify(record));
    } catch (error) {
      warn('Local consent storage could not be written.', error);
    }

    try {
      document.cookie = `${LOCAL_CONSENT_COOKIE}=${encoded}; Max-Age=${LOCAL_CONSENT_MAX_AGE}; Path=/; SameSite=Lax`;
    } catch (error) {
      warn('Local consent cookie could not be written.', error);
    }

    window.CorgiCookieConsentFallback = record;
    return record;
  }

  function hasExistingConsent(api) {
    if (readLocalConsent()) return true;
    if (!api || typeof api.currentVisitorConsent !== 'function') return false;

    const consent = api.currentVisitorConsent() || {};

    return CONSENT_KEYS.some((key) => consent[key] === 'yes' || consent[key] === 'no');
  }

  function getStorefrontAccessToken() {
    const features = document.getElementById('shopify-features');
    const checkoutToken = document.querySelector('meta[name="shopify-checkout-api-token"]');

    if (features?.textContent) {
      try {
        const parsedFeatures = JSON.parse(features.textContent);
        if (parsedFeatures?.accessToken) return parsedFeatures.accessToken;
      } catch (error) {
        warn('Shopify feature token could not be read.', error);
      }
    }

    return checkoutToken?.content || '';
  }

  function setBusy(root, isBusy, message) {
    const buttons = root.querySelectorAll(`${ACCEPT_SELECTOR}, ${DECLINE_SELECTOR}`);
    const status = root.querySelector(STATUS_SELECTOR);

    root.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    buttons.forEach((button) => {
      button.disabled = isBusy;
    });

    if (status) status.textContent = message || '';
  }

  function showBanner(root) {
    root.hidden = false;
    root.classList.remove(HIDING_CLASS);

    requestAnimationFrame(() => {
      root.classList.add(VISIBLE_CLASS);
    });
  }

  function hideBanner(root) {
    root.classList.remove(VISIBLE_CLASS);
    root.classList.add(HIDING_CLASS);

    window.setTimeout(() => {
      root.hidden = true;
      root.classList.remove(HIDING_CLASS);
      setBusy(root, false, '');
    }, 260);
  }

  function setTrackingConsent(api, consent) {
    return new Promise((resolve, reject) => {
      if (!api || typeof api.setTrackingConsent !== 'function') {
        reject(new Error('setTrackingConsent is not available.'));
        return;
      }

      const storefrontAccessToken = getStorefrontAccessToken();
      const consentPayload = storefrontAccessToken
        ? {
            ...consent,
            storefrontAccessToken,
          }
        : consent;

      try {
        api.setTrackingConsent(consentPayload, (result) => {
          if (result && result.error) {
            reject(new Error(Array.isArray(result.error) ? result.error.join(', ') : String(result.error)));
            return;
          }

          resolve(result);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  function bindActions(root, api) {
    const acceptButton = root.querySelector(ACCEPT_SELECTOR);
    const declineButton = root.querySelector(DECLINE_SELECTOR);

    async function handleConsent(consent, message) {
      setBusy(root, true, message);

      try {
        await setTrackingConsent(api, consent);
        hideBanner(root);
      } catch (error) {
        warn('Consent could not be saved with Shopify; using local fallback.', error);
        writeLocalConsent(consent);
        hideBanner(root);
      }
    }

    acceptButton?.addEventListener('click', () => {
      handleConsent(
        {
          analytics: true,
          marketing: true,
          preferences: true,
        },
        'Guardando preferencias de cookies aceptadas.'
      );
    });

    declineButton?.addEventListener('click', () => {
      handleConsent(
        {
          analytics: false,
          marketing: false,
          preferences: false,
        },
        'Guardando rechazo de cookies no esenciales.'
      );
    });
  }

  async function init() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    if (readLocalConsent()) {
      root.hidden = true;
      return;
    }

    try {
      const api = await loadCustomerPrivacy();

      if (hasExistingConsent(api)) {
        root.hidden = true;
        return;
      }

      bindActions(root, api);
      showBanner(root);
    } catch (error) {
      warn('Customer Privacy API failed to load; banner will use local fallback.', error);
      bindActions(root, null);
      showBanner(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    return;
  }

  init();
})();
